import { Link, useLocation } from "wouter";
import { clsx } from "clsx";
import { Camera, ClipboardList, MessageCircle, FileText, UserCircle, Shield } from "lucide-react";
import type { ReactNode } from "react";
import { Logo } from "./Logo";
import { useAuthOptional } from "@/auth/AuthProvider";

const NAV = [
  { href: "/foto", label: "Mi foto", Icon: Camera },
  { href: "/datos", label: "Mis datos", Icon: ClipboardList },
  { href: "/asesor", label: "Asesor", Icon: MessageCircle },
  { href: "/reportes", label: "Reportes", Icon: FileText },
];

/** Marco de la app: 4 destinos (barra inferior en móvil, header en desktop); Cuenta y Admin como íconos en el header. */
export function Shell({ children, demo = false }: { children: ReactNode; demo?: boolean }) {
  const [loc] = useLocation();
  const auth = useAuthOptional();
  const activo = (href: string) => loc === href || loc.startsWith(`${href}/`) || (href === "/datos" && loc === "/empezar");
  const ancho = loc.startsWith("/datos") || loc === "/foto" || loc === "/" ? "max-w-5xl" : "max-w-3xl";
  return (
    <div className="flex min-h-full flex-col">
      {demo && (
        <div className="bg-crema px-4 py-2 text-center text-sm text-negro print:hidden">
          Estás viendo el ejemplo de Henry. Nada se guarda. <Link href="~/" className="ml-1 font-semibold underline">Salir</Link>
        </div>
      )}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-negro/95 text-white backdrop-blur print:hidden">
        <div className={clsx("mx-auto flex h-14 items-center justify-between gap-2 px-4", ancho)}>
          <Link href="/foto" aria-label="Ir a Mi foto"><Logo tono="oscuro" /></Link>
          <nav className="hidden gap-1 md:flex" aria-label="Principal">
            {NAV.map(({ href, label }) => (
              <Link key={href} href={href} aria-current={activo(href) ? "page" : undefined} className={clsx("rounded-md px-3 py-2 text-sm font-medium transition-colors", activo(href) ? "bg-white/10 text-white" : "text-white/65 hover:text-white")}>{label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            {auth?.esAdmin && !demo && <Link href="/admin" aria-label="Admin" className={clsx("rounded-md p-2.5", activo("/admin") ? "bg-white/10 text-white" : "text-white/65 hover:text-white")}><Shield className="h-5 w-5" aria-hidden /></Link>}
            {!demo && <Link href="/cuenta" aria-label="Cuenta" className={clsx("rounded-md p-2.5", activo("/cuenta") ? "bg-white/10 text-white" : "text-white/65 hover:text-white")}><UserCircle className="h-5 w-5" aria-hidden /></Link>}
          </div>
        </div>
      </header>
      <main className={clsx("mx-auto w-full flex-1 px-4 pb-36 pt-4 md:pb-10", ancho)}>{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-borde bg-white md:hidden print:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} aria-label="Principal">
        <ul className="grid grid-cols-4">
          {NAV.map(({ href, label, Icon }) => (
            <li key={href}>
              <Link href={href} aria-current={activo(href) ? "page" : undefined} className={clsx("flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium", activo(href) ? "text-negro" : "text-tinta-suave")}>
                <Icon className="h-5 w-5" strokeWidth={activo(href) ? 2.2 : 1.6} aria-hidden /><span className={activo(href) ? "font-semibold" : undefined}>{label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
